import React, { useState, useEffect, useRef } from "react";
import { NJIconButton, NJIcon } from "@engie-group/fluid-design-system-react";
import { SidebarLink } from "@genai/sidebar";
import styles from "./chatSideBarItem.module.css";
import { updateChat } from "@genai/chat";
import { Chat, ChatId } from "@genai/chat";
import { AssistantId } from "@genai/assistant";
import { ConfirmationModal } from '@genai/confirmation';

const MAX_CHAR_INPUT_NAME = 29;

interface ChatSidebarItemProps {
  chat: Chat;
  chatId: ChatId | undefined;
  assistantId: AssistantId;
  toggleMenu: (uuid: string) => void;
  handleDelete: (assistantId: AssistantId, chatId: ChatId) => void;
  menuOpen: { [key: string]: boolean };
  setMenuOpen: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
}

export const ChatSidebarItem = ({
  chat,
  chatId,
  assistantId,
  toggleMenu,
  menuOpen,
  handleDelete,
  setMenuOpen,
}: ChatSidebarItemProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newChatName, setNewChatName] = useState(chat.name);
  const [isHovered, setIsHovered] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const menuButtonRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });

  useEffect(() => {
    if (menuOpen && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
  }, [menuOpen]);

  const handleRename = () => {
    updateChat(chat.assistant, chat.uuid, chat.collection, newChatName);
    setIsEditing(false);
    setMenuOpen((prev) => ({
      ...prev,
      [chat.uuid]: false,
    }));
  };

  return (
    <SidebarLink
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setMenuOpen((prev) => ({
          ...prev,
          [chat.uuid]: false,
        }));
      }}
      className={styles["genai-sidebar-link"]}
      key={chat.uuid}
      active={chatId === chat.uuid}
      to={`/assistant/${assistantId}/chat/${chat.uuid}`}
    >
      {isEditing ? (
        <input
          type="text"
          className={styles["genai-sidebar-input_name"]}
          value={newChatName}
          onChange={(e) => setNewChatName(e.target.value)}
          onBlur={handleRename}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleRename();
              setMenuOpen((prev) => ({
                ...prev,
                [chat.uuid]: false,
              }));
            } else if (e.key === "Escape") {
              setIsEditing(false);
              setNewChatName(chat.name);
              setMenuOpen((prev) => ({
                ...prev,
                [chat.uuid]: false,
              }));
            }
          }}
          autoFocus
        />
      ) : (
        <>
          <div title={newChatName}>
            {newChatName.length > MAX_CHAR_INPUT_NAME
              ? newChatName.substring(0, MAX_CHAR_INPUT_NAME) + "..."
              : newChatName}
          </div>
          {isHovered && (
            <div className={styles["menu-container"]}>
              <div
                ref={menuButtonRef}
                className={styles["menu-button"]}
                onClick={(e) => {
                  e.preventDefault();
                  toggleMenu(chat.uuid);
                }}
              >
                <NJIconButton
                  label="button"
                  type="button"
                  icon="more_horiz"
                  className={styles["icon-button"]}
                  size="xs"
                />
              </div>
              {menuOpen[chat.uuid] && (
                <>
                  <div
                    className={styles["dropdown-menu"]}
                    style={{
                      position: "fixed",
                      top: menuPosition.top,
                      left: menuPosition.left,
                    }}
                  >
                    <div
                      className={styles["dropdown-item"]}
                      onClick={() => {
                        setIsEditing(true);
                      }}
                    >
                      <NJIcon name="edit" size="sm" /> Renommer
                    </div>
                    <div
                      className={styles["dropdown-item"]}
                      onClick={setIsConfirmationModalOpen.bind(null, true)}
                    >
                      <NJIcon name="delete" size="sm" /> Supprimer
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      <ConfirmationModal
        content={
          "Êtes-vous sûr de vouloir supprimer ce chat ?<br/><br/>" +
          "Supprimer ce chat le retirera définitivement de votre liste de chats."
        }
        isOpen={isConfirmationModalOpen}
        onClose={(confirm) => {
          if (confirm) {
            handleDelete(assistantId, chat.uuid);
          }
          setIsConfirmationModalOpen(false);
        }}
      ></ConfirmationModal>
    </SidebarLink>
  );
};
